import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { UsesOfStars, getSunStar, getNakshatraName, getMonthMerit, getDayMerit, getThithiMerit, getYogaMerit, netraPhalan, jeevanPhalan, getKazaram, getVivahChakra, getKaalam } from '@/panchangam/services/dayvalue-helpers';
import { PANCHANGAM_API, getPoruththam } from '@/panchangam/services/panchangam-utils';

const T = { bg:'#f0f5e8', cream:'#fffef0', greenDk:'#1b5e20', maroon:'#6d1b1b', orange:'#e65100', header:'linear-gradient(135deg,#090b72,#1a237e)' };

const DayValuePage = () => {
  const [pd, setPd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [today, setToday] = useState(dayjs().format('YYYY-MM-DD'));
  const [calc, setCalc] = useState({});

  const fetchData = useCallback(async (dateStr) => {
    setLoading(true);
    try {
      const res = await axios.get(PANCHANGAM_API, { params: { date: dateStr, time: '12pm' } });
      if (res.data?.[0]) { setPd(res.data[0]); compute(res.data[0]); }
      else throw new Error('no data');
    } catch {
      try {
        const lr = await axios.get('/data/panchangam_data-2025-to-2028.json');
        const d = new Date(dateStr);
        const key = `${d.getMonth()+1}/${d.getDate()}/${d.getFullYear()}`;
        const found = lr.data.find(r => r.EnglishDate === key);
        if (found) { setPd(found); compute(found); } else setPd(null);
      } catch { setPd(null); }
    } finally { setLoading(false); }
  }, []);

  function compute(data) {
    const starInfo = UsesOfStars.find(x => x.StarNo === data.NakshatraNumber) || {};
    const dow = new Date(data.EnglishDate).getDay() + 1;
    const sunStarNo = getSunStar(data.Suriyan || 1);
    const kazaram = getKazaram(sunStarNo, data.NakshatraNumber);
    const vivah = getVivahChakra(sunStarNo, data.NakshatraNumber);
    const kaalam = getKaalam(data.SuryaUdhayam, data.AgasDuration);
    const porutham = getPoruththam(data.NakshatraNumber);
    const yoga1 = (data.CalcDayYogamsShortInTamil || '').substring(0, 2);

    setCalc({
      monthMerit: getMonthMerit(data.TamilMonthNumber),
      thithiMerit: getThithiMerit(data.Paksham, data.ThithiNumber),
      dayMerit: getDayMerit(dow),
      yogaMerit: getYogaMerit(yoga1),
      starMerit: starInfo.Merit || '',
      starType: starInfo.Type || '',
      naming: starInfo.Naming || '',
      starWorks: starInfo.Works || '',
      kazaram, vivah, kaalam, porutham,
      sunStarNo,
      nethram: netraPhalan(data.Nethram),
      jeevan: jeevanPhalan(data.Jeevan),
    });
  }

  useEffect(() => { fetchData(today); }, [today, fetchData]);
  const prev = () => setToday(dayjs(today).subtract(1,'day').format('YYYY-MM-DD'));
  const next = () => setToday(dayjs(today).add(1,'day').format('YYYY-MM-DD'));
  const fmt = v => v != null ? Number(v).toFixed(2) : '';
  const fmtDate = s => { if(!s) return ''; const d=new Date(s); return `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()}`; };

  if (loading) return <div style={{textAlign:'center',padding:'60px',color:'#090b72',fontWeight:700}}>Please wait....</div>;
  if (!pd) return <div style={{textAlign:'center',padding:'40px'}}><p>தரவு கிடைக்கவில்லை</p><button onClick={prev}>← முன்</button> <button onClick={next}>பின் →</button></div>;

  const c = calc;
  const meritColor = m => { if(!m) return '#333'; if(m.includes('மிகநன்று')) return '#1b5e20'; if(m.includes('நன்று')) return '#1565c0'; if(m.includes('விலக்கவும்')||m.includes('தவிர்க்கவும்')) return '#c62828'; return '#e65100'; };
  const V = c.vivah?.dirs || {};

  return (
    <div style={{background:T.bg,minHeight:'80vh'}}>
      {/* Header */}
      <div style={{width:'100%',backgroundColor:'#090b72',borderBottom:'2px solid orange',borderTop:'2px solid orange',display:'flex',alignItems:'center'}}>
        <div style={{width:'10%',textAlign:'center',cursor:'pointer',padding:'12px 0'}} onClick={prev}><span style={{color:'#fff',fontSize:'24px'}}>◀</span></div>
        <div style={{width:'80%',textAlign:'center',color:'#fff',fontWeight:700,fontSize:'2.2vh',padding:'8px 0'}}>
          {pd.YearNameInTamil}-{pd.AyanamNameInTamil}<br/>
          <span style={{color:'rgb(152,248,243)',fontSize:'0.9rem'}}>{pd.RuthuNameInTamil}-ருது-{pd.TamilMonth}-{pd.TamilDate}</span><br/>
          <span style={{color:'yellow',fontSize:'1rem'}}>{fmtDate(pd.EnglishDate)} - {pd.DayNameInTamil}</span>
        </div>
        <div style={{width:'10%',textAlign:'center',cursor:'pointer',padding:'12px 0'}} onClick={next}><span style={{color:'#fff',fontSize:'24px'}}>▶</span></div>
      </div>

      {/* நாள் மதிப்பு */}
      <div style={{textAlign:'center',backgroundColor:'#015540',color:'#fff',padding:'6px',fontWeight:700,fontSize:'0.9rem'}}>
        {fmtDate(pd.EnglishDate)} - தின மதிப்பு
      </div>
      <div style={{padding:'6px 10px',background:'#fff'}}>
        {[
          { n:1, t:`${pd.TamilMonth} - மாதம்`, m:c.monthMerit },
          { n:2, t:`${pd.PakshamNameInTamil?.substring(0,4)}-${pd.ThithiNameInTamil} (${fmt(pd.ThithiDuration)} வரை)`, m:c.thithiMerit },
          { n:3, t:`${pd.DayNameInTamil}-கிழமை`, m:c.dayMerit },
          { n:4, t:c.yogaMerit, m:'' },
          { n:5, t:`${pd.NakshatraNameInTamil} (${fmt(pd.NakshatraDuration)} வரை)`, m:c.starMerit },
          { n:6, t:`${fmt(pd.NakshatraDuration)} ${c.kazaram?.text} (சூரி-${c.sunStarNo}) கசரம்: ${c.kazaram?.value}`, m:'' },
          { n:7, t:c.nethram, m:'' },
          { n:8, t:c.jeevan, m:'' },
        ].map(r => (
          <div key={r.n} style={{padding:'4px 0',borderBottom:'1px solid #eee',fontSize:'0.8rem'}}>
            <span style={{color:T.greenDk,fontWeight:700}}>{r.n}) </span>
            {r.t} {r.m && <span style={{fontWeight:700,color:meritColor(r.m)}}> — {r.m}</span>}
          </div>
        ))}
      </div>

      {/* நக்ஷத்திர விவரம் */}
      <div style={{background:T.cream,padding:'8px 10px',margin:'4px 0',borderRadius:'6px',border:'1px solid #c8e6c9'}}>
        <div style={{fontSize:'0.75rem'}}><b style={{color:T.greenDk}}>நக்ஷத்ரம்:</b> {pd.NakshatraNameInTamil} - {c.starType}</div>
        <div style={{fontSize:'0.75rem',borderTop:'1px solid #eee',marginTop:'4px',paddingTop:'4px'}}><b style={{color:T.greenDk}}>பெயர் வைக்க எழுத்து:</b> {c.naming}</div>
        <div style={{fontSize:'0.75rem',borderTop:'1px solid #eee',marginTop:'4px',paddingTop:'4px'}}><b style={{color:T.greenDk}}>யாருக்கு பொருந்தும்:</b> {c.porutham}</div>
        <div style={{fontSize:'0.75rem',borderTop:'1px solid #eee',marginTop:'4px',paddingTop:'4px'}}><b style={{color:T.greenDk}}>{pd.NakshatraNameInTamil}*ல் செய்யத் தக்கவை:</b> {c.starWorks}</div>
      </div>
      <div style={{background:'#fff3e0',padding:'6px 10px',fontSize:'0.7rem',color:T.orange,fontWeight:600}}>
        மாத, திதி, கிழ, யோ, நக்ஷ 5ம் நன்றாக இருந்தால் மட்டும் திதி-நக்ஷத்திரம் முடிவதற்குள் மற்ற விதிப்படி சுபம் செய்யலாம்.
      </div>

      {/* 5 பெருங்காலம் */}
      <div style={{textAlign:'center',backgroundColor:'#015540',color:'#fff',padding:'6px',fontWeight:700,fontSize:'0.9rem'}}>
        {fmtDate(pd.EnglishDate)} - நாளின் 5ம் பெருங்காலம்
      </div>
      <div style={{background:'#fff'}}>
        {c.kaalam && ['prathah','sangava','madhyahna','aparahna','saayahna'].map((k,i) => (
          <div key={k} style={{display:'flex',borderBottom:'1px solid #eee',fontSize:'0.8rem'}}>
            <div style={{width:'30%',padding:'5px 8px',background:'#e8f5e9',fontWeight:700,color:T.greenDk}}>
              {['ப்ராத:காலம்','சங்கவ காலம்','மாத்யாஹ்னம்','அபராஹ்னம்','சாயாஹ்னம்'][i]}
            </div>
            <div style={{width:'70%',padding:'5px 8px'}}>{c.kaalam[k]}</div>
          </div>
        ))}
      </div>
      {/* Gandharva / Kutapa / Rouhina */}
      {c.kaalam && <div style={{background:'#e8f5e9',padding:'6px 10px',fontSize:'0.75rem',color:T.greenDk,fontWeight:600}}>
        கந்தர்வ காலம் : {c.kaalam.sangavaEnd} To {c.kaalam.kutapaFrom}<br/>
        குதப காலம் : {c.kaalam.kutapaFrom} To {c.kaalam.kutapaEnd}<br/>
        ரௌஹிண காலம் : {c.kaalam.kutapaEnd} To {c.kaalam.madhyahnaEnd}
      </div>}

      {/* விவாஹ சக்ரம் */}
      <div style={{textAlign:'center',backgroundColor:'#015540',color:'#fff',padding:'6px',fontWeight:700,fontSize:'0.9rem'}}>
        {fmtDate(pd.EnglishDate)} - விவாஹ சக்ரம்
      </div>
      <div style={{background:'darkgreen',color:'yellow',fontSize:'0.7rem'}}>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr'}}>
          <Cell bg="darkgreen" t={[V.neast1,V.neast2,V.neast3]} />
          <Cell bg="darkgreen" t={[V.east1,V.east2,V.east3]} />
          <Cell bg="#250505" t={[V.seast1,V.seast2,V.seast3]} />
          <Cell bg="darkgreen" t={[V.north1,V.north2,V.north3]} />
          <Cell bg="#250505" t={[V.mid1,V.mid2,V.mid3]} />
          <Cell bg="#250505" t={[V.south1,V.south2,V.south3]} />
          <Cell bg="#250505" t={[V.nwest1,V.nwest2,V.nwest3]} />
          <Cell bg="#250505" t={[V.west1,V.west2,V.west3]} />
          <Cell bg="darkgreen" t={[V.swest1,V.swest2,V.swest3]} />
        </div>
        <div style={{padding:'6px',fontSize:'0.7rem'}}>
          &nbsp;* பச்சை வண்ணத்தில் உள்ள நக்ஷத்திரங்கள் சுபமானவை<br/>
          {c.vivah?.result}
        </div>
      </div>

      {/* கிரஹ நிலை — South Indian Rasi Box */}
      <div style={{textAlign:'center',backgroundColor:'#015540',color:'#fff',padding:'6px',fontWeight:700,fontSize:'0.9rem'}}>
        {fmtDate(pd.EnglishDate)} - கிரஹ நிலை
      </div>
      <RasiBox gp={pd.GrahasPositionsShortInTamilStringified} />

      {/* Date picker */}
      <div style={{textAlign:'center',padding:'16px',fontSize:'0.9rem'}}>
        தேவை தேதிக்கு தகவல் பெற தேதி தேர்வு செய்யவும்
        <div style={{margin:'12px auto',maxWidth:'300px'}}>
          <input type="date" value={today} onChange={e=>setToday(e.target.value)}
            style={{width:'100%',padding:'10px',fontSize:'16px',borderRadius:'8px',border:'2px solid #090b72',textAlign:'center'}} />
        </div>
      </div>
    </div>
  );
};

const Cell = ({bg,t=[]}) => (
  <div style={{background:bg,padding:'6px',border:'1px solid #333',textAlign:'center',lineHeight:1.4}}>
    {t.map((v,i) => <div key={i}>{v||''}</div>)}
  </div>
);

// South Indian Rasi Box — 4×4 grid with center image
const RasiBox = ({gp}) => {
  if (!gp) return <div style={{padding:'10px',textAlign:'center',fontSize:'0.8rem',color:'#999'}}>கிரஹ நிலை தரவு கிடைக்கவில்லை</div>;
  const cs = {border:'1px solid #1b5e20',padding:'4px',fontSize:'0.7rem',color:'#1b5e20',textAlign:'center',background:'#f0f5e8',minHeight:'40px',display:'flex',alignItems:'center',justifyContent:'center'};
  const g = (n) => gp[n] || '';
  return (
    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',background:'#fff',border:'2px solid #1b5e20'}}>
      <div style={cs}>{g('12')}</div>
      <div style={cs}>{g('1')}</div>
      <div style={cs}>{g('2')}</div>
      <div style={cs}>{g('3')}</div>
      <div style={cs}>{g('11')}</div>
      <div style={{...cs,gridColumn:'span 2',gridRow:'span 2',background:'#fff',border:'1px solid #1b5e20'}}>
        <img src="/images/paranthaman.png" alt="பரந்தாமன்" style={{height:'80px',width:'auto'}} />
      </div>
      <div style={cs}>{g('4')}</div>
      <div style={cs}>{g('10')}</div>
      <div style={cs}>{g('5')}</div>
      <div style={cs}>{g('9')}</div>
      <div style={cs}>{g('8')}</div>
      <div style={cs}>{g('7')}</div>
      <div style={cs}>{g('6')}</div>
    </div>
  );
};

export default DayValuePage;
